import React, { useEffect, useState } from 'react';

import pluralize from 'pluralize';

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { Brand } from '@luxuryescapes/lib-regions/lib/regions';

import PermissionedComponent from '~/components/Common/PermissionedComponent';
import ScheduleTable from '~/components/Offers/Edit/common/ScheduleTable';
import { useScheduleSection } from '~/components/Offers/Edit/common/useScheduleSection';
import { useUndoableDelete } from '~/components/Offers/Edit/common/useUndoableDelete';

import { CUSTOMER_FACING_TYPES } from './campaignScheduleBrandRows';
import type { CampaignScheduleBrandRow } from './types';

interface Props {
  offerId: string;
  open: boolean;
  onClose: () => void;
  row: CampaignScheduleBrandRow | undefined;
}

export default function CampaignScheduleBrandGroupModal({ offerId, open, onClose, row }: Props) {
  const [isDeleteAllOpen, setIsDeleteAllOpen] = useState(false);
  const { onSaveFinished, onUpdateErrors, onDelete, onError, invalidateSchedules } = useScheduleSection({
    offerId,
    shouldMigrateSchedule: false,
  });

  // Keeps rendering the last-known row while the Dialog plays its closing transition, so it
  // doesn't flash empty/crash if `row` disappears out from under an open modal — e.g. "Delete All"
  // removing the last schedules of a LuxPlus-only group, which has no other type to keep it alive
  // in brandRows. `isOpen` below (not `open`) is what actually drives visibility.
  const [lastRow, setLastRow] = useState(row);
  useEffect(() => {
    if (row) {
      setLastRow(row);
    }
  }, [row]);

  const displayRow = row ?? lastRow;
  const isOpen = open && !!row;

  const editableSchedules =
    displayRow?.schedules.filter((schedule) => CUSTOMER_FACING_TYPES.includes(schedule.type)) ?? [];
  const brandLabel = displayRow?.brand ?? '';
  const types = CUSTOMER_FACING_TYPES.filter((type) => editableSchedules.some((schedule) => schedule.type === type));

  const {
    confirm: executeDeleteAll,
    isDeleting: isDeletingAll,
    isUndoWindowOpen: isDeleteAllUndoWindowOpen,
    cancel: cancelDeleteAll,
  } = useUndoableDelete({
    offerId,
    label: `All ${editableSchedules.length} ${pluralize('schedule', editableSchedules.length)} for ${brandLabel}`,
    getIdList: () => editableSchedules.map((schedule) => schedule.id),
    onSuccess: invalidateSchedules,
    onError,
  });

  if (!displayRow) {
    return null;
  }

  const brand = displayRow.schedules[0]?.brand as Brand | undefined;

  const confirmDeleteAll = () => {
    setIsDeleteAllOpen(false);
    executeDeleteAll();
  };

  return (
    <>
      <Dialog fullWidth maxWidth="md" open={isOpen} onClose={onClose}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {displayRow.brand} schedules
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <ScheduleTable
            brand={brand}
            offerId={offerId}
            schedules={displayRow.schedules}
            types={types}
            shouldMigrateSchedule={false}
            isDeletingAll={isDeletingAll}
            onSaveFinished={onSaveFinished}
            onDelete={onDelete}
            onUpdateErrors={onUpdateErrors}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', px: 3, pb: 2 }}>
          <PermissionedComponent>
            {isDeleteAllUndoWindowOpen ? (
              <Stack alignItems="center" direction="row" spacing={1}>
                <Typography color="text.secondary" variant="body2">
                  Deleting…
                </Typography>
                <Button size="small" onClick={cancelDeleteAll}>
                  Undo
                </Button>
              </Stack>
            ) : (
              <Button
                color="error"
                disabled={editableSchedules.length === 0 || isDeletingAll}
                variant="outlined"
                onClick={() => setIsDeleteAllOpen(true)}
              >
                {isDeletingAll ? 'Deleting…' : 'Delete All'}
              </Button>
            )}
          </PermissionedComponent>
        </DialogActions>
      </Dialog>
      <Dialog open={isDeleteAllOpen} onClose={() => setIsDeleteAllOpen(false)}>
        <DialogTitle>Delete all schedules for {brandLabel}?</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography>
              This will delete all {editableSchedules.length} {pluralize('schedule', editableSchedules.length)} for{' '}
              {brandLabel}. You will have 5 seconds to undo.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteAllOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteAll} autoFocus>
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
